import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DynamicObjectInterface } from '../../../../sdk/type';
import {
  Button,
  ButtonIconPosition,
  ButtonSize,
  ButtonStyles,
  ButtonTypes,
  Card,
  DropDown,
  DropDownDataInterface,
  DropDownValidations,
  FAIcon,
  IconStyle,
  Input,
  InputTypes,
  InputValidations,
  MLPVersion,
  Modal,
  TextAlign,
  Theme,
} from '../../../../ui/components';
import styles from '../../teams/edit/participant-details/index.module.scss';
import { REACT_APP_COUNTRY } from '../../../../envVars';
import { CampaignData } from './[recId]';

// Age groups must match the participant ageGroup values stored on the user table.
export const ageGroupList = ['18-39', '40-59', '60+'];
export const genderThresholdList: Array<'MALE' | 'FEMALE'> = ['MALE', 'FEMALE'];

export interface DetailsModalProps {
  onSave?: DynamicObjectInterface;
  onCancel?: DynamicObjectInterface;
  campaignData: CampaignData | null;
  handleOnChange?: (value: DynamicObjectInterface, key: string, ...paths: string[]) => void;
  formData?: CampaignData | null;
  /** When provided, modal scopes its edits to activities.subChallenges[i] instead of legacy paths. */
  subChallengeIndex?: number;
}

export const indicatorWinningCriteriaList = [
  { value: '', title: '' },
  { value: 'gain', title: '+' },
  { value: 'loss', title: '-' },
];

export const indicatorPrerequisiteCriteriaList = [
  { value: '', title: '' },
  { value: 'above', title: 'Above' },
  { value: 'below', title: 'Below' },
];

export const ruleOperatorList = [
  { value: 'AND', title: 'AND' },
  { value: 'OR', title: 'OR' },
];

export const ruleTypeList = [
  { value: 'BUNDLE', title: 'BUNDLE' },
  { value: 'BASE', title: 'BASE' },
];

export const WinningCriteriaModal = ({ onSave, onCancel, campaignData, subChallengeIndex }: DetailsModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CampaignData | null>(
    JSON.parse(JSON.stringify(campaignData)) as CampaignData,
  );
  const [showDelete, setShowDelete] = useState(-1);

  // When subChallengeIndex is provided, scope all reads/writes to that sub-challenge's
  // rules[4].subKpiCode. Otherwise fall back to the legacy participant.rules[4] path.
  const isSubChallenge = typeof subChallengeIndex === 'number';
  const basePath = isSubChallenge
    ? `activities.subChallenges.${subChallengeIndex}.rules.4.subKpiCode`
    : `activities.participant.rules.4.subKpiCode`;

  const getSubKpiList = (data: CampaignData | null) => {
    if (!data) return undefined;
    if (isSubChallenge) {
      return data.activities?.subChallenges?.[subChallengeIndex!]?.rules?.[4]?.subKpiCode;
    }
    return data.activities?.participant?.rules?.[4]?.subKpiCode;
  };

  // Test devices are configured per sub-challenge only. When a device list exists,
  // each indicator can be tagged to a device (e.g. "Pores - Shop Device" vs
  // "Pores - Skin Analyser") so the participant's chosen device decides which
  // +/- variant is shown and judged. Empty list => device feature off (legacy).
  const testDevicesPath = `activities.subChallenges.${subChallengeIndex}.testDevices`;
  const getTestDevices = (data: CampaignData | null): DynamicObjectInterface[] =>
    (isSubChallenge ? data?.activities?.subChallenges?.[subChallengeIndex!]?.testDevices : undefined) ?? [];

  const handleAddDevice = () => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      const sc = newData.activities?.subChallenges?.[subChallengeIndex!];
      if (!sc) return prev;
      if (!sc.testDevices) sc.testDevices = [];
      sc.testDevices.push({ code: `DEVICE_${Date.now()}`, name: '' });
      return newData;
    });
  };

  const handleDeleteDevice = (deviceIndex: number) => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      const sc = newData.activities?.subChallenges?.[subChallengeIndex!];
      if (sc?.testDevices) sc.testDevices.splice(deviceIndex, 1);
      return newData;
    });
  };

  const handleOnChange = (value: DynamicObjectInterface, path: string) => {
    setFormData(prev => {
      if (!prev) return prev;
      const paths = path.split('.');
      const newData = JSON.parse(JSON.stringify(prev));
      let current = newData;

      for (let i = 0; i < paths.length - 1; i++) {
        const key = paths[i];
        current[key] = Array.isArray(current[key]) ? [...current[key]] : { ...current[key] };
        current = current[key];
      }

      const lastKey = paths[paths.length - 1];
      current[lastKey] = value;
      return newData;
    });
  };

  const handleAdd = () => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));

      if (isSubChallenge) {
        const sc = newData.activities?.subChallenges?.[subChallengeIndex!];
        if (!sc) return prev;
        if (!sc.rules) sc.rules = [];
        if (!sc.rules[4]) sc.rules[4] = { subKpiCode: [] };
        sc.rules[4].subKpiCode.push({ subKpiCode: `TEMP-${Date.now()}` });
      } else {
        if (!newData.activities.participant.rules) {
          newData.activities.participant.rules = [];
        }
        if (!newData.activities.participant.rules[4]) {
          newData.activities.participant.rules[4] = { subKpiCode: [] };
        }
        newData.activities.participant.rules[4].subKpiCode.push({ subKpiCode: `TEMP-${Date.now()}` });
      }

      return newData;
    });
  };

  const handleDeleteButton = (index: number) => {
    setShowDelete(index);
  };

  const handleDelete = (index: number) => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));

      if (isSubChallenge) {
        const sc = newData.activities?.subChallenges?.[subChallengeIndex!];
        if (sc?.rules?.[4]?.subKpiCode) sc.rules[4].subKpiCode.splice(index, 1);
      } else if (newData.activities.participant.rules?.[4]?.subKpiCode) {
        newData.activities.participant.rules[4].subKpiCode.splice(index, 1);
      }

      return newData;
    });
    setShowDelete(-1);
  };

  const deletePrompt = (
    <>
      {showDelete !== -1 && (
        <Modal
          primaryButtonLabel={''}
          headline={`${t('admin.campaignConfig.deleteIndicator')}`}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('admin.rewardMaster.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                handleDelete(showDelete);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.rewardMaster.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowDelete(-1);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.BLACK}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );
  return (
    <Modal
      primaryButtonLabel={''}
      headline={t('admin.campaignConfig.editWinningPercentage')}
      textAlign={TextAlign.LEFT}
      closeIcon={false}
      containerWidth={'1200px'}
      primaryButton={
        <Button
          title={t('admin.participant.save')}
          type={ButtonTypes.BUTTON}
          onClick={() => onSave(formData)}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.SOLID}
          data-testid="test-add-button-popup"
        />
      }
      secondaryButton={
        <Button
          title={t('admin.participant.cancel')}
          type={ButtonTypes.BUTTON}
          onClick={onCancel}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          theme={Theme.BLACK}
          data-testid="test-cancel-button-popup-delete-modal"
        />
      }
    >
      <div
        className={`${styles['modal-reward']} ${
          getSubKpiList(formData) && (getSubKpiList(formData)?.length ?? 0) > 0 ? styles['increased-height'] : ''
        }`}
      >
        {isSubChallenge && (
          <Card>
            <div className={styles['modal-reward-headline']}>
              <div>{'Test Devices (optional)'}</div>
            </div>
            <div>
              {
                'Add the measurement devices for this challenge. Tag each indicator below to a device so the participant picks one device and only its indicators apply. Leave empty to disable device selection.'
              }
            </div>
            {getTestDevices(formData).map((device, deviceIndex) => (
              <div className={styles['modal-details-form']} key={`device-${deviceIndex}`}>
                <Input
                  name={`device-name-${deviceIndex}`}
                  value={device.name ?? ''}
                  mlpVersion={MLPVersion.ONE}
                  label={'Device Name'}
                  onChange={e => handleOnChange(e.target.value, `${testDevicesPath}.${deviceIndex}.name`)}
                  required={true}
                />
                <div className={styles['modal-order-rule-section']}>
                  <Button
                    title={'Remove'}
                    type={ButtonTypes.BUTTON}
                    onClick={() => handleDeleteDevice(deviceIndex)}
                    size={ButtonSize.SMALL}
                    buttonStyle={ButtonStyles.OUTLINE}
                    theme={Theme.RED}
                  />
                </div>
              </div>
            ))}
            <Button
              title={'Add Device'}
              type={ButtonTypes.BUTTON}
              onClick={handleAddDevice}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
            />
          </Card>
        )}
        {getSubKpiList(formData)?.map((item, index) => (
          <Card key={`indicator-${index}`}>
            <div className={styles['modal-reward-headline']}>
              <div>
                {t('admin.campaignConfig.indicatorLabel')} {index + 1}
              </div>

              <FAIcon
                iconName={'trash-can'}
                onClick={e => {
                  handleDeleteButton(index);
                  e.stopPropagation();
                }}
                theme={Theme.RED}
                title={'Delete'}
                className={styles['modal-reward-delete']}
              />
            </div>
            <div className={styles['modal-details-form']}>
              <Input
                name={'indicatorName'}
                value={item.indicatorName}
                mlpVersion={MLPVersion.ONE}
                label={t('admin.campaignConfig.indicatorName')}
                onChange={e =>
                  handleOnChange(e.target.value, `${basePath}.${index}.indicatorName`)
                }
                required={true}
              />
              <DropDown
                name={'indicatorWinningCriteria'}
                dropdownLabel={t('admin.campaignConfig.indicatorWinningCriteria')}
                placeholder={t('admin.campaignConfig.indicatorWinningCriteria')}
                value={indicatorWinningCriteriaList.find(i => i.value === item.indicatorWinningCriteria)?.title}
                mlpVersion={MLPVersion.ONE}
                data={indicatorWinningCriteriaList}
                configData={{
                  label: 'title',
                  onSelect: (selectedOption: DropDownDataInterface) => {
                    handleOnChange(
                      selectedOption.value,
                      `${basePath}.${index}.indicatorWinningCriteria`,
                    );
                  },
                }}
                required={true}
              />
              {/* Tag this indicator to a configured device (blank = any device). */}
              {isSubChallenge && getTestDevices(formData).length > 0 && (
                <DropDown
                  name={`indicatorTestDevice-${index}`}
                  dropdownLabel={'Test Device'}
                  placeholder={'Any device'}
                  value={getTestDevices(formData).find(d => d.code === item.testDevice)?.name ?? 'Any device'}
                  mlpVersion={MLPVersion.ONE}
                  data={[{ code: '', name: 'Any device' }, ...getTestDevices(formData)]}
                  configData={{
                    label: 'name',
                    onSelect: (selectedOption: DropDownDataInterface) => {
                      handleOnChange(
                        selectedOption.code || undefined,
                        `${basePath}.${index}.testDevice`,
                      );
                    },
                  }}
                />
              )}
              <Input
                name={'indicatorValue'}
                type={InputTypes.NUMBER}
                value={item.targetFrom ?? ''}
                mlpVersion={MLPVersion.ONE}
                label={t('admin.campaignConfig.indicatorValue')}
                onChange={e =>
                  handleOnChange(
                    e.target.value.toString(),
                    `${basePath}.${index}.targetFrom`,
                  )
                }
                required={true}
              />
              <Input
                name={'indicatorMin'}
                type={InputTypes.NUMBER}
                value={item.indicatorMin ?? ''}
                mlpVersion={MLPVersion.ONE}
                label={t('admin.campaignConfig.indicatorMin')}
                onChange={e =>
                  handleOnChange(
                    e.target.value.toString(),
                    `${basePath}.${index}.indicatorMin`,
                  )
                }
              />
              <Input
                name={'indicatorMax'}
                type={InputTypes.NUMBER}
                value={item.indicatorMax ?? ''}
                mlpVersion={MLPVersion.ONE}
                label={t('admin.campaignConfig.indicatorMax')}
                onChange={e =>
                  handleOnChange(
                    e.target.value.toString(),
                    `${basePath}.${index}.indicatorMax`,
                  )
                }
              />
              <Input
                name={'indicatorMeasurementUnit'}
                value={item.indicatorMeasurementUnit}
                mlpVersion={MLPVersion.ONE}
                label={t('admin.campaignConfig.indicatorMeasurementUnit')}
                onChange={e =>
                  handleOnChange(
                    e.target.value,
                    `${basePath}.${index}.indicatorMeasurementUnit`,
                  )
                }
                required={true}
              />
              <Input
                name={'indicatorPlaceholder'}
                value={item.indicatorPlaceholder}
                mlpVersion={MLPVersion.ONE}
                label={t('admin.campaignConfig.indicatorPlaceholder')}
                onChange={e =>
                  handleOnChange(
                    e.target.value,
                    `${basePath}.${index}.indicatorPlaceholder`,
                  )
                }
              />

              <DropDown
                name={'indicatorPrerequisiteCriteria'}
                dropdownLabel={t('admin.campaignConfig.indicatorPrerequisiteCriteria')}
                placeholder={t('admin.campaignConfig.indicatorPrerequisiteCriteria')}
                value={
                  indicatorPrerequisiteCriteriaList.find(i => i.value === item.indicatorPrerequisiteCriteria)?.title
                }
                mlpVersion={MLPVersion.ONE}
                data={indicatorPrerequisiteCriteriaList}
                configData={{
                  label: 'title',
                  onSelect: (selectedOption: DropDownDataInterface) => {
                    handleOnChange(
                      selectedOption.value,
                      `${basePath}.${index}.indicatorPrerequisiteCriteria`,
                    );
                  },
                }}
              />

              <Input
                name={'indicatorPrerequisite'}
                type={InputTypes.NUMBER}
                value={item.indicatorPrerequisite ?? ''}
                mlpVersion={MLPVersion.ONE}
                label={t('admin.campaignConfig.indicatorPrerequisite')}
                onChange={e =>
                  handleOnChange(
                    e.target.value.toString(),
                    `${basePath}.${index}.indicatorPrerequisite`,
                  )
                }
              />
            </div>

            {/* Checkboxes in their own segment (a clean 3-column grid) so they no
                longer share, and overlap, the input fields' grid. */}
            <div className={styles['indicator-checkbox-grid']}>
              {/* Must Hit: this indicator must pass for the participant to win, on top
                  of meeting the "indicators to win" count. */}
              <div className={styles['checkbox-container']} key={`checkbox-${index}-mustHit`}>
                <input
                  id={`checkbox-${index}-mustHit`}
                  className={styles['checkbox-container-checkbox']}
                  type="checkbox"
                  checked={item.mustHit === 1}
                  onChange={() => handleOnChange(item.mustHit ? 0 : 1, `${basePath}.${index}.mustHit`)}
                />
                <label
                  className={styles['checkbox-container-label-bold']}
                  htmlFor={`checkbox-${index}-mustHit`}
                >
                  Must Hit (mandatory to win)
                </label>
              </div>

              <div className={styles['checkbox-container']} key={`checkbox-${index}-percentage`}>
                <input
                  id={`checkbox-${index}-percentage`}
                  className={styles['checkbox-container-checkbox']}
                  type="checkbox"
                  checked={item.percentageCalculation === 1}
                  onChange={() =>
                    handleOnChange(
                      item.percentageCalculation ? 0 : 1,
                      `${basePath}.${index}.percentageCalculation`,
                    )
                  }
                />
                <label
                  className={styles['checkbox-container-label-bold']}
                  htmlFor={`checkbox-${index}-percentage`}
                >
                  Use Percentage Calculation
                </label>
              </div>

              <div className={styles['checkbox-container']} key={`checkbox-${index}-progress`}>
                <input
                  id={`checkbox-${index}-progress`}
                  className={styles['checkbox-container-checkbox']}
                  type="checkbox"
                  checked={item.displayInProgressMonitoring === 'on'}
                  onChange={() =>
                    handleOnChange(
                      item.displayInProgressMonitoring ? '' : 'on',
                      `${basePath}.${index}.displayInProgressMonitoring`,
                    )
                  }
                />
                <label
                  className={styles['checkbox-container-label-bold']}
                  htmlFor={`checkbox-${index}-progress`}
                >
                  Display in Progress Monitoring Section
                </label>
              </div>

              <div className={styles['checkbox-container']} key={`checkbox-${index}-preAssessment`}>
                <input
                  id={`checkbox-${index}-preAssessment`}
                  className={styles['checkbox-container-checkbox']}
                  type="checkbox"
                  checked={item.disablePreAssessment === 1}
                  onChange={() =>
                    handleOnChange(
                      item.disablePreAssessment ? 0 : 1,
                      `${basePath}.${index}.disablePreAssessment`,
                    )
                  }
                />
                <label
                  className={styles['checkbox-container-label-bold']}
                  htmlFor={`checkbox-${index}-preAssessment`}
                >
                  Disable in Pre-Assessment Section
                </label>
              </div>

              {/* Field stays editable in the pre-assessment form, but winning is judged
                  against the post-assessment value only (the pre value is ignored). */}
              <div className={styles['checkbox-container']} key={`checkbox-${index}-ignorePreAssessment`}>
                <input
                  id={`checkbox-${index}-ignorePreAssessment`}
                  className={styles['checkbox-container-checkbox']}
                  type="checkbox"
                  checked={item.ignorePreAssessment === 1}
                  onChange={() =>
                    handleOnChange(
                      item.ignorePreAssessment ? 0 : 1,
                      `${basePath}.${index}.ignorePreAssessment`,
                    )
                  }
                />
                <label
                  className={styles['checkbox-container-label-bold']}
                  htmlFor={`checkbox-${index}-ignorePreAssessment`}
                >
                  Ignore Pre-Assessment Value
                </label>
              </div>

              {REACT_APP_COUNTRY === 'MY' && (
                <div
                  className={styles['checkbox-container']}
                  key={`checkbox-${index}-genderAgeThreshold`}
                >
                  <input
                    id={`checkbox-${index}-genderAgeThreshold`}
                    className={styles['checkbox-container-checkbox']}
                    type="checkbox"
                    checked={!!item.genderAgeThresholds}
                    onChange={() =>
                      handleOnChange(
                        item.genderAgeThresholds
                          ? undefined
                          : { MALE: {}, FEMALE: {} },
                        `${basePath}.${index}.genderAgeThresholds`,
                      )
                    }
                  />
                  <label
                    className={styles['checkbox-container-label-bold']}
                    htmlFor={`checkbox-${index}-genderAgeThreshold`}
                  >
                    Use Gender / Age Group Threshold
                  </label>
                </div>
              )}
            </div>

            {REACT_APP_COUNTRY === 'MY' && item.genderAgeThresholds && (
              <div className={styles['gender-age-threshold']}>
                  {genderThresholdList.map(gender => (
                    <div key={`${index}-${gender}`} className={styles['gender-age-threshold-group']}>
                      <label className={styles['checkbox-container-label-bold']}>{gender}</label>
                      <div className={styles['gender-age-threshold-row']}>
                        {ageGroupList.map(ageGroup => (
                          <Input
                            key={`${index}-${gender}-${ageGroup}`}
                            name={`genderAgeThreshold-${gender}-${ageGroup}`}
                            type={InputTypes.NUMBER}
                            mlpVersion={MLPVersion.ONE}
                            value={item.genderAgeThresholds?.[gender]?.[ageGroup] ?? ''}
                            placeholder={ageGroup}
                            label={ageGroup}
                            onChange={e =>
                              handleOnChange(
                                e.target.value.toString(),
                                `${basePath}.${index}.genderAgeThresholds.${gender}.${ageGroup}`,
                              )
                            }
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            {deletePrompt}
          </Card>
        ))}
        <Button
          title={t('admin.campaignConfig.addWinningCriteria')}
          type={ButtonTypes.BUTTON}
          onClick={handleAdd}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          data-testid="test-add-button-popup"
        />
      </div>
    </Modal>
  );
};

export const FileUploaderModal = ({ onSave, onCancel, campaignData, subChallengeIndex }: DetailsModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CampaignData | null>(
    JSON.parse(JSON.stringify(campaignData)) as CampaignData,
  );
  const [showDelete, setShowDelete] = useState(-1);

  // When subChallengeIndex is provided, scope all reads/writes to that sub-challenge's
  // fileUploader. Otherwise fall back to the legacy participant.fileUploader path.
  const isSubChallenge = typeof subChallengeIndex === 'number';
  const basePath = isSubChallenge
    ? `activities.subChallenges.${subChallengeIndex}.fileUploader`
    : `activities.participant.fileUploader`;

  const getFileUploaderList = (data: CampaignData | null) => {
    if (!data) return undefined;
    if (isSubChallenge) {
      return data.activities?.subChallenges?.[subChallengeIndex!]?.fileUploader;
    }
    return data.activities?.participant?.fileUploader;
  };

  const handleOnChange = (value: DynamicObjectInterface, path: string) => {
    setFormData(prev => {
      if (!prev) return prev;
      const paths = path.split('.');
      const newData = JSON.parse(JSON.stringify(prev));
      let current = newData;

      for (let i = 0; i < paths.length - 1; i++) {
        const key = paths[i];
        current[key] = Array.isArray(current[key]) ? [...current[key]] : { ...current[key] };
        current = current[key];
      }

      const lastKey = paths[paths.length - 1];
      current[lastKey] = value;
      return newData;
    });
  };

  const handleAdd = () => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));

      if (isSubChallenge) {
        const sc = newData.activities?.subChallenges?.[subChallengeIndex!];
        if (!sc) return prev;
        if (!sc.fileUploader) sc.fileUploader = [];
        sc.fileUploader.push({ fieldCode: `TEMP-${Date.now()}` });
      } else {
        // Ensure the rules array structure exists
        if (!newData.activities?.participant?.fileUploader) {
          newData.activities.participant.fileUploader = [];
        }
        newData.activities.participant.fileUploader.push({
          fieldCode: `TEMP-${Date.now()}`,
        });
      }

      return newData;
    });
  };

  const handleDeleteButton = (index: number) => {
    setShowDelete(index);
  };

  const handleDelete = (index: number) => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));

      if (isSubChallenge) {
        const sc = newData.activities?.subChallenges?.[subChallengeIndex!];
        if (sc?.fileUploader) sc.fileUploader.splice(index, 1);
      } else if (newData.activities.participant.fileUploader) {
        newData.activities.participant.fileUploader.splice(index, 1);
      }

      return newData;
    });
    setShowDelete(-1);
  };

  const deletePrompt = (
    <>
      {showDelete !== -1 && (
        <Modal
          primaryButtonLabel={''}
          headline={`Are you sure you want to delete the file type? This action cannot be undone!`}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('admin.rewardMaster.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                handleDelete(showDelete);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.rewardMaster.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowDelete(-1);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.BLACK}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );
  return (
    <Modal
      primaryButtonLabel={''}
      headline={`Edit Assessment File Upload`}
      textAlign={TextAlign.LEFT}
      closeIcon={false}
      containerWidth={'1200px'}
      primaryButton={
        <Button
          title={t('admin.participant.save')}
          type={ButtonTypes.BUTTON}
          onClick={() => onSave(formData)}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.SOLID}
          data-testid="test-add-button-popup"
        />
      }
      secondaryButton={
        <Button
          title={t('admin.participant.cancel')}
          type={ButtonTypes.BUTTON}
          onClick={onCancel}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          theme={Theme.BLACK}
          data-testid="test-cancel-button-popup-delete-modal"
        />
      }
    >
      <div className={`${styles['modal-reward']}`}>
        {getFileUploaderList(formData)?.map((item, index) => (
          <Card key={item.fieldCode}>
            <div className={styles['modal-reward-headline']}>
              <div>File Type {index + 1}</div>

              <FAIcon
                iconName={'trash-can'}
                onClick={e => {
                  handleDeleteButton(index);
                  e.stopPropagation();
                }}
                theme={Theme.RED}
                title={'Delete'}
                className={styles['modal-reward-delete']}
              />
            </div>
            <div className={styles['modal-details-form']}>
              <Input
                name={'fieldName'}
                value={item.fieldName}
                mlpVersion={MLPVersion.ONE}
                label={'Field Name'}
                onChange={e => handleOnChange(e.target.value, `${basePath}.${index}.fieldName`)}
                required={true}
              />
              <Input
                name={'numberOfFile'}
                type={InputTypes.NUMBER}
                value={item.numberOfFile ?? ''}
                mlpVersion={MLPVersion.ONE}
                label={'Number (how many file uploader required)'}
                onChange={e => handleOnChange(e.target.value.toString(), `${basePath}.${index}.numberOfFile`)}
              />
            </div>
            {/* Only require/allow this upload during the post-assessment (hidden in pre). */}
            <div className={styles['checkbox-container']} key={`fileUploader-${index}-postOnly`}>
              <input
                id={`fileUploader-${index}-postOnly`}
                className={styles['checkbox-container-checkbox']}
                type="checkbox"
                checked={item.postAssessmentOnly === 1}
                onChange={() =>
                  handleOnChange(item.postAssessmentOnly ? 0 : 1, `${basePath}.${index}.postAssessmentOnly`)
                }
              />
              <label className={styles['checkbox-container-label-bold']} htmlFor={`fileUploader-${index}-postOnly`}>
                Upload in Post-Assessment Section Only
              </label>
            </div>
            {deletePrompt}
          </Card>
        ))}
        <Button
          title={'Add File Type'}
          type={ButtonTypes.BUTTON}
          onClick={handleAdd}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          data-testid="test-add-button-popup"
        />
      </div>
    </Modal>
  );
};

export const CheckInModal = ({ onSave, onCancel, campaignData, subChallengeIndex }: DetailsModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CampaignData | null>(
    JSON.parse(JSON.stringify(campaignData)) as CampaignData,
  );
  const [showDelete, setShowDelete] = useState(-1);

  // Check-in is configured per sub-challenge only (MULTI / MY). basePath scopes
  // every read/write to that sub-challenge's checkIn object.
  const isSubChallenge = typeof subChallengeIndex === 'number';
  const basePath = `activities.subChallenges.${subChallengeIndex}.checkIn`;

  // Participating-days window (activities.participant.validity.value), NOT the full
  // event period (end - start). A check-in day must fall within the actual number of
  // days a participant takes part.
  const campaignDurationDays = Number(campaignData?.activities?.participant?.validity?.value) || 0;

  const getCheckIn = (data: CampaignData | null) =>
    isSubChallenge ? data?.activities?.subChallenges?.[subChallengeIndex!]?.checkIn : undefined;

  const getDays = (data: CampaignData | null): (number | string)[] => getCheckIn(data)?.days ?? [];
  const getFileUploaderList = (data: CampaignData | null): DynamicObjectInterface[] =>
    getCheckIn(data)?.fileUploader ?? [];

  const ensureCheckIn = (newData: CampaignData): { days: (number | string)[]; fileUploader: DynamicObjectInterface[] } => {
    const sc = newData.activities?.subChallenges?.[subChallengeIndex!] as DynamicObjectInterface;
    if (!sc.checkIn) sc.checkIn = { days: [], fileUploader: [] };
    if (!sc.checkIn.days) sc.checkIn.days = [];
    if (!sc.checkIn.fileUploader) sc.checkIn.fileUploader = [];
    return sc.checkIn as { days: (number | string)[]; fileUploader: DynamicObjectInterface[] };
  };

  const handleOnChange = (value: DynamicObjectInterface, path: string) => {
    setFormData(prev => {
      if (!prev) return prev;
      const paths = path.split('.');
      const newData = JSON.parse(JSON.stringify(prev));
      let current = newData;
      for (let i = 0; i < paths.length - 1; i++) {
        const key = paths[i];
        current[key] = Array.isArray(current[key]) ? [...current[key]] : { ...current[key] };
        current = current[key];
      }
      current[paths[paths.length - 1]] = value;
      return newData;
    });
  };

  const handleDayChange = (index: number, raw: string) => {
    const num = raw === '' ? '' : Number(raw);
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      const checkIn = ensureCheckIn(newData);
      checkIn.days[index] = num;
      return newData;
    });
  };

  const handleAddDay = () => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      ensureCheckIn(newData).days.push('');
      return newData;
    });
  };

  const handleDeleteDay = (index: number) => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      ensureCheckIn(newData).days.splice(index, 1);
      return newData;
    });
  };

  const handleAddFile = () => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      ensureCheckIn(newData).fileUploader.push({ fieldCode: `TEMP-${Date.now()}` });
      return newData;
    });
  };

  const handleDeleteFile = (index: number) => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      ensureCheckIn(newData).fileUploader.splice(index, 1);
      return newData;
    });
    setShowDelete(-1);
  };

  const days = getDays(formData);
  const dayValueInvalid = (d: number | string) =>
    d === '' || isNaN(Number(d)) || Number(d) < 1 || (campaignDurationDays > 0 && Number(d) > campaignDurationDays);
  const hasInvalidDay = days.some(dayValueInvalid);
  const hasDuplicateDay = new Set(days.filter(d => d !== '').map(Number)).size !== days.filter(d => d !== '').length;

  const deletePrompt = (
    <>
      {showDelete !== -1 && (
        <Modal
          primaryButtonLabel={''}
          headline={`Are you sure you want to delete this check-in file type? This action cannot be undone!`}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('admin.rewardMaster.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => handleDeleteFile(showDelete)}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
            />
          }
          secondaryButton={
            <Button
              title={t('admin.rewardMaster.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => setShowDelete(-1)}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.BLACK}
            />
          }
        />
      )}
    </>
  );

  return (
    <Modal
      primaryButtonLabel={''}
      headline={`Edit Check-In Settings`}
      textAlign={TextAlign.LEFT}
      closeIcon={false}
      containerWidth={'1200px'}
      primaryButton={
        <Button
          title={t('admin.participant.save')}
          type={ButtonTypes.BUTTON}
          onClick={() => onSave(formData)}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.SOLID}
          disabled={hasInvalidDay || hasDuplicateDay}
        />
      }
      secondaryButton={
        <Button
          title={t('admin.participant.cancel')}
          type={ButtonTypes.BUTTON}
          onClick={onCancel}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          theme={Theme.BLACK}
        />
      }
    >
      <div className={`${styles['modal-reward']}`}>
        <div>
          {`Check-in days (day number within the campaign, 1 - ${campaignDurationDays || '?'}). The form opens on each configured day.`}
        </div>
        {days.map((day, index) => (
          <Card key={`checkin-day-${index}`}>
            <div className={styles['modal-reward-headline']}>
              <div>Check-In {index + 1}</div>
              <FAIcon
                iconName={'trash-can'}
                onClick={e => {
                  handleDeleteDay(index);
                  e.stopPropagation();
                }}
                theme={Theme.RED}
                title={'Delete'}
                className={styles['modal-reward-delete']}
              />
            </div>
            <div className={styles['modal-details-form']}>
              <Input
                name={`checkin-day-${index}`}
                type={InputTypes.NUMBER}
                value={day === '' ? '' : `${day}`}
                mlpVersion={MLPVersion.ONE}
                label={'Day of campaign'}
                onChange={e => handleDayChange(index, e.target.value)}
                required={true}
                status={dayValueInvalid(day) ? InputValidations.INVALID : InputValidations.DEFAULT}
              />
            </div>
          </Card>
        ))}
        <Button
          title={'Add Check-In Day'}
          type={ButtonTypes.BUTTON}
          onClick={handleAddDay}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
        />

        <hr />
        <div>{'Check-in proof upload (applies to every check-in day)'}</div>
        {getFileUploaderList(formData).map((item, index) => (
          <Card key={item.fieldCode}>
            <div className={styles['modal-reward-headline']}>
              <div>File Type {index + 1}</div>
              <FAIcon
                iconName={'trash-can'}
                onClick={e => {
                  setShowDelete(index);
                  e.stopPropagation();
                }}
                theme={Theme.RED}
                title={'Delete'}
                className={styles['modal-reward-delete']}
              />
            </div>
            <div className={styles['modal-details-form']}>
              <Input
                name={'fieldName'}
                value={item.fieldName}
                mlpVersion={MLPVersion.ONE}
                label={'Field Name'}
                onChange={e => handleOnChange(e.target.value, `${basePath}.fileUploader.${index}.fieldName`)}
                required={true}
              />
              <Input
                name={'numberOfFile'}
                type={InputTypes.NUMBER}
                value={item.numberOfFile ?? ''}
                mlpVersion={MLPVersion.ONE}
                label={'Number (how many file uploader required)'}
                onChange={e => handleOnChange(e.target.value.toString(), `${basePath}.fileUploader.${index}.numberOfFile`)}
              />
            </div>
            {deletePrompt}
          </Card>
        ))}
        <Button
          title={'Add File Type'}
          type={ButtonTypes.BUTTON}
          onClick={handleAddFile}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
        />
      </div>
    </Modal>
  );
};

export const OrderRuleModal = ({ onSave, onCancel, campaignData, subChallengeIndex }: DetailsModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CampaignData | null>(
    JSON.parse(JSON.stringify(campaignData)) as CampaignData,
  );
  const [showDelete, setShowDelete] = useState(false);
  const [deletedPosition, setDeletedPosition] = useState(-1);
  const [deletedGroupPosition, setDeletedGroupPosition] = useState(-1);

  // When subChallengeIndex is provided, scope reads/writes to that sub-challenge's
  // order.rules. Otherwise fall back to the legacy activities.order.rules path.
  const isSubChallenge = typeof subChallengeIndex === 'number';
  const basePath = isSubChallenge
    ? `activities.subChallenges.${subChallengeIndex}.order.rules`
    : `activities.order.rules`;

  const getOrderRules = (data: CampaignData | null) => {
    if (!data) return undefined;
    if (isSubChallenge) {
      return data.activities?.subChallenges?.[subChallengeIndex!]?.order?.rules;
    }
    return data.activities?.order?.rules;
  };

  const writeOrderRules = (data: DynamicObjectInterface, groups: DynamicObjectInterface[][]) => {
    if (isSubChallenge) {
      if (!data.activities.subChallenges) data.activities.subChallenges = [];
      if (!data.activities.subChallenges[subChallengeIndex!]) return;
      if (!data.activities.subChallenges[subChallengeIndex!].order) {
        data.activities.subChallenges[subChallengeIndex!].order = { rules: [], maxOrderNumber: 0, orderPeriod: 0 };
      }
      data.activities.subChallenges[subChallengeIndex!].order.rules = groups;
    } else {
      data.activities.order.rules = groups;
    }
  };

  const handleOnChange = (value: DynamicObjectInterface, path: string) => {
    setFormData(prev => {
      if (!prev) return prev;
      const paths = path.split('.');
      const newData = JSON.parse(JSON.stringify(prev));
      let current = newData;

      for (let i = 0; i < paths.length - 1; i++) {
        const key = paths[i];
        current[key] = Array.isArray(current[key]) ? [...current[key]] : { ...current[key] };
        current = current[key];
      }

      const lastKey = paths[paths.length - 1];
      current[lastKey] = value;
      return newData;
    });
  };

  const handleAdd = (groupIndex?: number, position?: number) => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      const allGroups = getOrderRules(newData) ?? [];

      // Ensure the targeted group exists
      const targetGroup = allGroups[groupIndex ?? 0] ?? [];

      const newRule = {
        type: '',
        operator: '',
        value: '',
      };

      const insertIndex = typeof position === 'number' && position >= 0 ? position : targetGroup.length;

      // Insert into the selected group
      targetGroup.splice(insertIndex + 1, 0, newRule);

      // Reassign updated group back into rules
      allGroups[groupIndex ?? 0] = targetGroup;
      writeOrderRules(newData, allGroups);

      return newData;
    });
  };

  const handleDeleteButton = (groupIndex: number, position?: number) => {
    setShowDelete(true);
    setDeletedGroupPosition(groupIndex);
    setDeletedPosition(position ?? -1);
  };

  const handleDelete = () => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      const allGroups = getOrderRules(newData) ?? [];

      // Guard against invalid indexes
      if (
        deletedGroupPosition >= 0 &&
        deletedGroupPosition < allGroups.length &&
        Array.isArray(allGroups[deletedGroupPosition])
      ) {
        if (
          deletedPosition !== -1 &&
          deletedPosition >= 0 &&
          deletedPosition < allGroups[deletedGroupPosition].length
        ) {
          // Delete specific rule in the group
          allGroups[deletedGroupPosition].splice(deletedPosition, 1);

          // Optionally remove the group if it becomes empty
          if (allGroups[deletedGroupPosition].length === 0) {
            allGroups.splice(deletedGroupPosition, 1);
          }
        } else {
          // Delete the entire group
          allGroups.splice(deletedGroupPosition, 1);
        }

        writeOrderRules(newData, allGroups);
      }

      return newData;
    });
    setShowDelete(false);
    setDeletedGroupPosition(0);
    setDeletedPosition(0);
  };

  const deletePrompt = (
    <>
      {showDelete && (
        <Modal
          primaryButtonLabel={''}
          headline={`${t('admin.campaignConfig.deleteRule')}`}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('admin.rewardMaster.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                handleDelete();
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.rewardMaster.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowDelete(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.BLACK}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );

  return (
    <Modal
      primaryButtonLabel={''}
      headline={t('admin.campaignConfig.editOrderRule')}
      textAlign={TextAlign.LEFT}
      closeIcon={false}
      containerWidth={'1200px'}
      primaryButton={
        <Button
          title={t('admin.participant.save')}
          type={ButtonTypes.BUTTON}
          onClick={() => onSave(formData)}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.SOLID}
          data-testid="test-add-button-popup"
        />
      }
      secondaryButton={
        <Button
          title={t('admin.participant.cancel')}
          type={ButtonTypes.BUTTON}
          onClick={onCancel}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          theme={Theme.BLACK}
          data-testid="test-cancel-button-popup-delete-modal"
        />
      }
    >
      <div className={`${styles['modal-reward']}`}>
        {getOrderRules(formData)?.map((record, index) => (
          <Card key={`rule-${index + 1}`}>
            <div className={styles['modal-reward-headline']}>
              <div>
                {t('admin.campaignConfig.rule')} {index + 1}
              </div>
              <FAIcon
                iconName={'trash-can'}
                onClick={e => {
                  handleDeleteButton(index);
                  e.stopPropagation();
                }}
                theme={Theme.RED}
                title={'Delete'}
                className={styles['modal-reward-delete']}
              />
            </div>

            {deletePrompt}

            {Array.isArray(record) &&
              record.map((item, itemIndex) => (
                <div className={styles['modal-details-form-5']}>
                  {itemIndex > 0 ? (
                    <DropDown
                      name={'operator'}
                      dropdownLabel={t('admin.campaignConfig.operator')}
                      placeholder={t('admin.campaignConfig.operator')}
                      value={ruleOperatorList.find(i => i.value === item.operator)?.title}
                      mlpVersion={MLPVersion.ONE}
                      data={ruleOperatorList}
                      configData={{
                        label: 'title',
                        onSelect: (selectedOption: DropDownDataInterface) => {
                          handleOnChange(selectedOption.value, `${basePath}.${index}.${itemIndex}.operator`);
                        },
                      }}
                      required={true}
                      status={item.operator ? DropDownValidations.DEFAULT : DropDownValidations.INVALID}
                    />
                  ) : (
                    <div></div>
                  )}

                  <DropDown
                    name={'type'}
                    dropdownLabel={t('admin.campaignConfig.type')}
                    placeholder={t('admin.campaignConfig.type')}
                    value={ruleTypeList.find(i => i.value === item.type)?.title}
                    mlpVersion={MLPVersion.ONE}
                    data={ruleTypeList}
                    configData={{
                      label: 'title',
                      onSelect: (selectedOption: DropDownDataInterface) => {
                        handleOnChange(selectedOption.value, `${basePath}.${index}.${itemIndex}.type`);
                      },
                    }}
                    required={true}
                    status={item.type ? DropDownValidations.DEFAULT : DropDownValidations.INVALID}
                  />

                  <Input
                    name={'productSKU'}
                    value={Array.isArray(item.value) ? item.value.join(',') : item.value}
                    mlpVersion={MLPVersion.ONE}
                    label={t('admin.campaignConfig.productSKU')}
                    onChange={e => {
                      const inputValue = e.target.value;
                      const valuesArray = inputValue.split(',').map(item => item.trim());
                      handleOnChange(valuesArray, `${basePath}.${index}.${itemIndex}.value`);
                    }}
                    required={true}
                    status={
                      Array.isArray(item.value) && item.value.length > 0 && !item.value.some(v => v === '')
                        ? InputValidations.DEFAULT
                        : InputValidations.INVALID
                    }
                  />

                  <div className={styles['modal-order-rule-section']}>
                    <Button
                      type={ButtonTypes.BUTTON}
                      onClick={() => handleAdd(index, itemIndex)}
                      iconPosition={ButtonIconPosition.LEFT}
                      icon={{ iconName: 'plus' }}
                      size={ButtonSize.SMALL}
                      buttonStyle={ButtonStyles.OUTLINE}
                      className={styles['modal-order-rule-button']}
                      style={{ marginLeft: '20px' }}
                      data-testid="test-add-button-popup"
                    />
                  </div>

                  <div className={styles['modal-order-rule-section']}>
                    <Button
                      type={ButtonTypes.BUTTON}
                      onClick={e => {
                        handleDeleteButton(index, itemIndex);
                        e.stopPropagation();
                      }}
                      iconPosition={ButtonIconPosition.LEFT}
                      icon={{ iconName: 'minus', iconStyle: IconStyle.SOLID }}
                      size={ButtonSize.SMALL}
                      buttonStyle={ButtonStyles.OUTLINE}
                      className={styles['modal-order-rule-button']}
                      style={{ marginRight: '20px' }}
                      data-testid="test-add-button-popup"
                    />
                  </div>
                </div>
              ))}
          </Card>
        ))}
        <Button
          title={t('admin.campaignConfig.addRule')}
          type={ButtonTypes.BUTTON}
          onClick={() => handleAdd(getOrderRules(formData)?.length)}
          iconPosition={ButtonIconPosition.LEFT}
          icon={{ iconName: 'plus' }}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          data-testid="test-add-button-popup"
        />
      </div>
    </Modal>
  );
};
